<template>
  <div class="space-y-4">

    <!-- Progress Bar (hidden on intro) -->
    <div v-if="phase !== 'intro'" class="w-full bg-gray-200 rounded-full h-2">
      <div
        class="bg-emerald-500 h-2 rounded-full transition-all duration-500"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>

    <!-- INTRO PHASE -->
    <div v-if="phase === 'intro'" class="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-b from-emerald-50/80 to-white p-8 sm:p-10 text-center shadow-sm">
      <!-- Framed book icon keeps the books front and centre -->
      <div class="mx-auto grid place-items-center w-20 h-20 rounded-2xl bg-emerald-100 ring-1 ring-emerald-200 text-4xl mb-5 shadow-sm">
        📚
      </div>
      <h2 class="text-2xl font-bold text-gray-900">Ready to learn?</h2>
      <p class="text-sm text-gray-400 mt-1.5">Quick checks pop up every few words to help it stick.</p>

      <!-- Count chips -->
      <div class="mt-5 flex flex-wrap items-center justify-center gap-2">
        <span class="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-3.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm">
          📖 {{ vocab.length }} word{{ vocab.length !== 1 ? 's' : '' }}
        </span>
        <span v-if="grammar.length" class="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-3.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm">
          ✏️ {{ grammar.length }} grammar concept{{ grammar.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <div class="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
        <button
          @click="advance"
          class="bg-emerald-600 text-white px-8 py-3 rounded-xl font-semibold shadow-sm hover:bg-emerald-700 hover:shadow hover:-translate-y-0.5 transition-all"
        >
          Start Learning &rarr;
        </button>
        <a
          v-if="!demoMode"
          :href="practiceUrl"
          class="bg-white text-gray-700 border border-gray-300 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-400 transition-colors"
        >
          Practice This Unit
        </a>
      </div>
    </div>

    <!-- VOCAB PHASE -->
    <div v-else-if="phase === 'vocab' && currentWord" class="bg-white rounded-lg border border-gray-200 p-6">
      <p class="text-xs text-gray-400 mb-4">Word {{ wordIndex + 1 }} of {{ vocab.length }}</p>

      <div class="flex items-center justify-between mb-1">
        <h2 class="text-3xl font-bold text-gray-900">{{ currentWord.word_vietnamese }}</h2>
        <span class="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{{ currentWord.part_of_speech }}</span>
      </div>
      <div class="flex items-center gap-2 mb-1">
        <!-- Skip the "pronounced" line when the romanization just repeats the word
             (true for most later-unit rows) — showing the same string twice reads
             like a bug. The PhoneticGuide below still carries the pronunciation. -->
        <p v-if="showRomanization" class="text-sm text-gray-500">
          <span class="text-[10px] uppercase tracking-wide text-gray-400 mr-1">{{ currentWord.part_of_speech === 'letter' ? 'called' : 'pronounced' }}</span>{{ currentWord.word_romanization }}
        </p>
        <AudioButton ref="wordAudio" :src="currentWord.audio_url || `/audio/vocab/${currentWord.id}_word.wav`" :show-slow="true" />
      </div>
      <!-- For letters the sound IS the lesson, so show it up front. The IPA phonetic
           guide is unreliable for single letters (it often spells the letter's name),
           so it's hidden for letters and kept for ordinary vocab words. -->
      <p v-if="currentWord.part_of_speech === 'letter'" class="text-sm text-emerald-700 font-medium mb-3">
        <span class="text-[10px] uppercase tracking-wide text-emerald-600 mr-1">sound</span>{{ currentWord.word_english.replace(' sound', '') }}
      </p>
      <PhoneticGuide v-else :ipa="currentWord.phonetic_ipa" :simplified="currentWord.phonetic_simplified" class="mb-3" />

      <!-- Revealed section -->
      <div v-if="revealed" class="border-t border-gray-100 pt-4 space-y-3">
        <p v-if="currentWord.part_of_speech !== 'letter'" class="text-lg font-semibold text-emerald-700">{{ currentWord.word_english }}</p>

        <div v-if="currentWord.example_sentence_vn">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Example</span>
            <AudioButton :src="currentWord.example_audio_url || `/audio/vocab/${currentWord.id}_example.wav`" />
          </div>
          <p class="text-sm text-gray-900 mt-0.5">{{ currentWord.example_sentence_vn }}</p>
          <p class="text-sm text-gray-500 mt-0.5">{{ currentWord.example_sentence_en }}</p>
        </div>

        <div
          v-if="currentWord.southern_pronunciation_notes"
          class="bg-amber-50 text-amber-800 px-3 py-2 rounded text-xs"
        >
          Southern note: {{ currentWord.southern_pronunciation_notes }}
        </div>
      </div>

      <!-- Action buttons -->
      <div class="mt-6">
        <button
          v-if="!revealed"
          @click="revealed = true"
          class="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
        >
          Reveal meaning
        </button>
        <button
          v-else
          @click="advance"
          class="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
        >
          Got it &rarr;
        </button>
      </div>
    </div>

    <!-- QUIZ PHASE -->
    <div v-else-if="phase === 'quiz'" class="bg-white rounded-lg border border-gray-200 p-6">
      <p class="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2">Quick check</p>

      <!-- Multiple Choice: "What does [Vietnamese] mean?" -->
      <template v-if="quizType === 'multiple_choice' && quizWord">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">
          What does "<span class="text-emerald-700">{{ quizWord.word_vietnamese }}</span>" mean?
        </h2>
        <div class="space-y-3">
          <button
            v-for="option in quizOptions"
            :key="option.wordId"
            @click="selectQuizOption(option)"
            :disabled="quizAnswered"
            :class="[
              'w-full text-left p-4 rounded-lg border-2 transition-colors',
              !quizAnswered
                ? 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                : option.isCorrect
                  ? 'border-green-500 bg-green-50 text-green-900'
                  : option.wordId === quizSelectedId
                    ? 'border-red-400 bg-red-50 text-red-900'
                    : 'border-gray-200 text-gray-400'
            ]"
          >
            {{ option.text }}
          </button>
        </div>
        <div v-if="quizAnswered" class="mt-4">
          <p v-if="quizSelectedId === quizWord.id" class="text-sm text-green-700 font-medium">Correct!</p>
          <p v-else class="text-sm text-red-700 font-medium">
            The correct answer is "{{ quizWord.word_english }}".
          </p>
          <WordRecap :word="quizWord" />
          <button @click="advance" class="mt-4 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors">
            Continue &rarr;
          </button>
        </div>
      </template>

      <!-- Reverse Recall: "Which Vietnamese word means [English]?" -->
      <template v-else-if="quizType === 'reverse_recall' && quizWord">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">
          Which Vietnamese word means "<span class="text-emerald-700">{{ quizWord.word_english }}</span>"?
        </h2>
        <div class="space-y-3">
          <button
            v-for="option in quizOptions"
            :key="option.wordId"
            @click="selectQuizOption(option)"
            :disabled="quizAnswered"
            :class="[
              'w-full text-left p-4 rounded-lg border-2 transition-colors',
              !quizAnswered
                ? 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                : option.isCorrect
                  ? 'border-green-500 bg-green-50 text-green-900'
                  : option.wordId === quizSelectedId
                    ? 'border-red-400 bg-red-50 text-red-900'
                    : 'border-gray-200 text-gray-400'
            ]"
          >
            {{ option.text }}
          </button>
        </div>
        <div v-if="quizAnswered" class="mt-4">
          <p v-if="quizSelectedId === quizWord.id" class="text-sm text-green-700 font-medium">Correct!</p>
          <p v-else class="text-sm text-red-700 font-medium">
            The correct answer is "{{ quizWord.word_vietnamese }}".
          </p>
          <WordRecap :word="quizWord" />
          <button @click="advance" class="mt-4 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors">
            Continue &rarr;
          </button>
        </div>
      </template>

      <!-- Fill in the Blank -->
      <template v-else-if="quizType === 'fill_blank' && quizWord">
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Fill in the blank</h2>
        <p class="text-lg text-gray-900 mb-2 font-medium">{{ fillSentence }}</p>
        <p class="text-sm text-gray-400 italic mb-6">{{ quizWord.example_sentence_en }}</p>
        <div class="space-y-3">
          <button
            v-for="option in fillOptions"
            :key="option.text"
            @click="selectFillOption(option)"
            :disabled="fillAnswered"
            :class="[
              'w-full text-left p-4 rounded-lg border-2 transition-colors',
              !fillAnswered
                ? 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                : option.isCorrect
                  ? 'border-green-500 bg-green-50 text-green-900'
                  : option.text === fillSelectedText
                    ? 'border-red-400 bg-red-50 text-red-900'
                    : 'border-gray-200 text-gray-400'
            ]"
          >
            {{ option.text }}
          </button>
        </div>
        <div v-if="fillAnswered" class="mt-4">
          <p v-if="fillSelectedText === quizWord.word_vietnamese" class="text-sm text-green-700 font-medium">Correct!</p>
          <p v-else class="text-sm text-red-700 font-medium">
            The correct answer is "{{ quizWord.word_vietnamese }}".
          </p>
          <WordRecap :word="quizWord" />
          <button @click="advance" class="mt-4 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors">
            Continue &rarr;
          </button>
        </div>
      </template>

      <!-- Type Answer (pure recall) -->
      <template v-else-if="quizType === 'type_answer' && quizWord">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">
          Type the Vietnamese word for "<span class="text-emerald-700">{{ quizWord.word_english }}</span>"
        </h2>
        <VietnameseInput
          v-model="typeAnswerText"
          @enter="submitTypeAnswer"
          :disabled="typeAnswerAnswered"
          class="w-full border-2 border-gray-200 rounded-lg p-4 text-lg focus:border-emerald-400 focus:outline-none"
          placeholder="Type the Vietnamese word..."
        />
        <button
          v-if="!typeAnswerAnswered"
          @click="submitTypeAnswer"
          :disabled="!typeAnswerText.trim()"
          class="mt-3 bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Check
        </button>
        <div v-if="typeAnswerAnswered" class="mt-4">
          <p v-if="typeAnswerCorrect" class="text-sm text-green-700 font-medium">Correct!</p>
          <p v-else class="text-sm text-red-700 font-medium">
            The correct answer is "{{ quizWord.word_vietnamese }}".
          </p>
          <p v-if="typeAnswerCorrect && typeAnswerToneMismatch" class="text-sm text-amber-700 mt-1">
            Watch your tones &rarr; <span class="font-medium">{{ quizWord.word_vietnamese }}</span>
          </p>
          <WordRecap :word="quizWord" />
          <button @click="advance" class="mt-4 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors">
            Continue &rarr;
          </button>
        </div>
      </template>

      <!-- Match Pairs -->
      <template v-else-if="quizType === 'match_pairs'">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">Match the pairs</h2>
        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Vietnamese</p>
            <button
              v-for="pair in matchVnOrder"
              :key="'vn-' + pair.id"
              @click="selectMatch('vn', pair)"
              :disabled="pair.matched"
              :class="[
                'w-full text-left p-3 rounded-lg border-2 transition-colors text-sm font-medium',
                pair.matched
                  ? 'border-green-300 bg-green-50 text-green-700 cursor-default'
                  : matchSelected?.type === 'vn' && matchSelected?.id === pair.id
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
              ]"
            >
              {{ pair.vietnamese }}
            </button>
          </div>
          <div class="space-y-2">
            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">English</p>
            <button
              v-for="pair in matchEnOrder"
              :key="'en-' + pair.id"
              @click="selectMatch('en', pair)"
              :disabled="pair.matched"
              :class="[
                'w-full text-left p-3 rounded-lg border-2 transition-colors text-sm font-medium',
                pair.matched
                  ? 'border-green-300 bg-green-50 text-green-700 cursor-default'
                  : matchSelected?.type === 'en' && matchSelected?.id === pair.id
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
              ]"
            >
              {{ pair.english }}
            </button>
          </div>
        </div>
        <div v-if="matchCompleted" class="mt-4">
          <p class="text-sm text-green-700 font-medium">All matched!</p>
          <button @click="advance" class="mt-4 bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors">
            Continue &rarr;
          </button>
        </div>
      </template>
    </div>

    <!-- GRAMMAR PHASE -->
    <div v-else-if="phase === 'grammar' && currentConcept">
      <p class="text-xs text-gray-400 mb-3">
        Grammar {{ grammarIndex + 1 }} of {{ grammar.length }}
      </p>
      <GrammarCard :concept="currentConcept" />
      <div class="mt-4">
        <button
          @click="advance"
          class="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
        >
          {{ grammarIndex < grammar.length - 1 ? 'Next &rarr;' : 'Finish &rarr;' }}
        </button>
      </div>
    </div>

    <!-- COMPLETE PHASE -->
    <div v-else-if="phase === 'complete'" class="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <div class="text-4xl mb-4">🎉</div>
      <h2 class="text-2xl font-bold text-gray-900">Lesson complete!</h2>
      <p class="text-gray-500 mt-2">
        You learned {{ vocab.length }} word{{ vocab.length !== 1 ? 's' : '' }}
        <span v-if="grammar.length"> and {{ grammar.length }} grammar concept{{ grammar.length !== 1 ? 's' : '' }}</span>.
      </p>

      <!-- Demo: there's no full unit to practise/browse — fold straight into the
           short exercise set so the taste stays one continuous flow. -->
      <div v-if="demoMode" class="mt-8 flex justify-center">
        <button
          @click="$emit('done')"
          class="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
        >
          Practice these words &rarr;
        </button>
      </div>
      <div v-else class="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a
          :href="practiceUrl"
          class="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
        >
          Practice This Unit
        </a>
        <button
          @click="$emit('browse')"
          class="bg-white text-gray-700 border border-gray-300 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          Browse All Words
        </button>
      </div>
    </div>

    <!-- Escape hatch mid-lesson: learners who already know these words can jump
         straight to exercises instead of paging through every card. (The demo
         has its own skip link in DemoFlow.) Placed after the phase chain so it
         doesn't break the v-if/v-else-if sequence above. -->
    <p
      v-if="!demoMode && practiceUrl && phase !== 'intro' && phase !== 'complete'"
      class="text-center pt-1"
    >
      <a
        :href="practiceUrl"
        class="inline-block bg-white text-emerald-700 border-2 border-emerald-600 px-8 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors shadow-sm"
      >
        Skip to practice &rarr;
      </a>
    </p>

  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import AudioButton from './shared/AudioButton.vue'
import GrammarCard from './GrammarCard.vue'
import PhoneticGuide from './shared/PhoneticGuide.vue'
import VietnameseInput from './shared/VietnameseInput.vue'
import WordRecap from './shared/WordRecap.vue'
import { checkVietnameseAnswer } from '../utils/similarity'
import { romanizationDiffers } from '../utils/vocab'
import { autoplayEnabled } from '../utils/autoplayPref'
import { apiPost } from '../api/client'

const props = defineProps({
  vocab:       { type: Array,  required: true },
  grammar:     { type: Array,  required: true },
  // Optional outside demo mode, which replaces the practise/browse links with a
  // single "done" hand-off into the exercise set.
  practiceUrl: { type: String, default: null },
  unitId:      { type: Number, default: null },
  level:       { type: Number, default: 1 },
  demoMode:    { type: Boolean, default: false },
  // True when the learner already completed this unit's walkthrough once.
  // Revisits skip the intro and use recall-style cards (tap to reveal); first
  // visits show meanings immediately — you can't recall a word you've never seen.
  revisit:     { type: Boolean, default: false },
})

const emit = defineEmits(['browse', 'done', 'progress'])

// Demo visitors already clicked "Try a lesson" (a second "Ready to learn?"
// gate just burns their patience) and revisiting learners have seen the intro
// before — both drop straight into the first word.
function initialPhase() {
  if (!props.demoMode && !props.revisit) return 'intro'
  if (props.vocab.length) return 'vocab'
  if (props.grammar.length) return 'grammar'
  return 'complete'
}
const phase        = ref(initialPhase())

// Meanings are shown immediately except on revisits, where the tap-to-reveal
// is deliberate retrieval practice for words the learner has met before.
const autoReveal   = props.demoMode || !props.revisit

// Record that the walkthrough was finished so the dashboard can show "Learned".
watch(phase, (p) => {
  if (p === 'complete' && props.unitId) {
    apiPost('/api/v1/progress/mark_learned', { unit_id: props.unitId }).catch(() => {})
  }
})
const wordIndex    = ref(0)
const grammarIndex = ref(0)
const revealed     = ref(autoReveal)

const quizWord       = ref(null)
const quizOptions    = ref([])
const quizAnswered   = ref(false)
const quizSelectedId = ref(null)
const quizType       = ref('multiple_choice')
const lastQuizType   = ref(null)
const learnedWords   = ref([])

// Fill-in-the-blank state
const fillSentence     = ref('')
const fillOptions      = ref([])
const fillAnswered     = ref(false)
const fillSelectedText = ref(null)

// Type answer state (pure recall)
const typeAnswerText     = ref('')
const typeAnswerAnswered = ref(false)
const typeAnswerCorrect  = ref(false)
const typeAnswerToneMismatch = ref(false)
const strictTones = (() => {
  try { return localStorage.getItem('speaksaigon_strict_tones') !== 'false' } catch (e) { return true }
})()

// Match pairs state
const matchPairs     = ref([])
const matchVnOrder   = ref([])
const matchEnOrder   = ref([])
const matchSelected  = ref(null)
const matchCompleted = ref(false)

const wordAudio      = ref(null)
const currentWord    = computed(() => props.vocab[wordIndex.value] || null)
const currentConcept = computed(() => props.grammar[grammarIndex.value] || null)

// Hide the "pronounced" line when the romanization is just the word repeated.
const showRomanization = computed(() => romanizationDiffers(currentWord.value))

// Demo mode has no intro screen, so kick off the first word's audio on mount.
// (Browsers may block autoplay without a gesture; the visible button is the fallback.)
onMounted(() => {
  if ((props.demoMode || props.revisit) && props.vocab.length && autoplayEnabled.value) {
    nextTick(() => wordAudio.value?.play())
  }
})

// Auto-play word audio when a new vocab word appears
watch(wordIndex, async () => {
  if (phase.value === 'vocab' && autoplayEnabled.value) {
    await nextTick()
    wordAudio.value?.play()
  }
})

const totalSteps = computed(() => props.vocab.length + props.grammar.length)

const completedSteps = computed(() => {
  if (phase.value === 'intro') return 0
  if (phase.value === 'vocab' || phase.value === 'quiz') return wordIndex.value
  if (phase.value === 'grammar') return props.vocab.length + grammarIndex.value
  if (phase.value === 'complete') return totalSteps.value
  return 0
})

const progressPercent = computed(() =>
  totalSteps.value === 0 ? 100 : Math.round((completedSteps.value / totalSteps.value) * 100)
)

// --- Quiz frequency & type selection ---

function shouldTriggerQuiz(completedCount, total) {
  if (completedCount === total) return true
  // Demo: keep momentum — one early check to prove the format, one at the end.
  if (props.demoMode) return completedCount === 2
  // Beginners (levels 1-2): a check after word 2, then every third word.
  // Quizzing after every single early word made lessons drag.
  if (props.level <= 2) {
    if (completedCount <= 3) return completedCount === 2
    return completedCount % 3 === 0
  }
  // At higher levels, quiz less frequently (trust the learner more)
  if (props.level >= 5) {
    if (completedCount <= 3) return completedCount % 2 === 0
    return completedCount % 3 === 0
  }
  if (completedCount <= 3) return true
  if (completedCount <= 6) return completedCount % 2 === 0
  return completedCount % 3 === 0
}

function pickQuizType() {
  const candidates = ['multiple_choice', 'reverse_recall']
  const latest = learnedWords.value[learnedWords.value.length - 1]
  if (latest?.example_sentence_vn?.includes(latest.word_vietnamese)) {
    candidates.push('fill_blank')
  }
  if (learnedWords.value.length >= 3) {
    candidates.push('match_pairs')
  }
  // At level 3+, add pure recall (type the Vietnamese word)
  if (props.level >= 3) {
    candidates.push('type_answer')
  }
  const filtered = candidates.filter(t => t !== lastQuizType.value)
  const pool = filtered.length > 0 ? filtered : candidates
  const chosen = pool[Math.floor(Math.random() * pool.length)]
  lastQuizType.value = chosen
  return chosen
}

// --- Quiz builders ---

function buildMultipleChoiceQuiz(targetWord, allVocab) {
  const pool = allVocab.filter(w => w.id !== targetWord.id)
  let distractorPool = pool
  // At level 3+, prefer same part-of-speech distractors for harder quizzes
  if (props.level >= 3) {
    const samePOS = pool.filter(w => w.part_of_speech === targetWord.part_of_speech)
    if (samePOS.length >= 3) distractorPool = samePOS
  }
  const distractors = [...distractorPool].sort(() => Math.random() - 0.5).slice(0, Math.min(3, pool.length))
  const options = [
    { text: targetWord.word_english, wordId: targetWord.id, isCorrect: true },
    ...distractors.map(w => ({ text: w.word_english, wordId: w.id, isCorrect: false })),
  ]
  return options.sort(() => Math.random() - 0.5)
}

function buildReverseRecallQuiz(targetWord, allVocab) {
  const pool = allVocab.filter(w => w.id !== targetWord.id)
  const distractors = [...pool].sort(() => Math.random() - 0.5).slice(0, Math.min(3, pool.length))
  const options = [
    { text: targetWord.word_vietnamese, wordId: targetWord.id, isCorrect: true },
    ...distractors.map(w => ({ text: w.word_vietnamese, wordId: w.id, isCorrect: false })),
  ]
  return options.sort(() => Math.random() - 0.5)
}

function buildFillBlankQuiz(targetWord, allVocab) {
  const blanked = targetWord.example_sentence_vn.replace(targetWord.word_vietnamese, '______')
  const samePOS = allVocab.filter(w => w.id !== targetWord.id && w.part_of_speech === targetWord.part_of_speech)
  const pool = samePOS.length >= 3 ? samePOS : allVocab.filter(w => w.id !== targetWord.id)
  const distractors = [...pool].sort(() => Math.random() - 0.5).slice(0, Math.min(3, pool.length))
  const options = [
    { text: targetWord.word_vietnamese, isCorrect: true },
    ...distractors.map(w => ({ text: w.word_vietnamese, isCorrect: false })),
  ].sort(() => Math.random() - 0.5)
  return { sentence: blanked, options }
}

function buildMatchPairsQuiz(words) {
  // More pairs at higher levels: 4 at levels 1-3, 5 at level 4, 6 at level 5+
  const maxPairs = props.level >= 5 ? 6 : props.level >= 4 ? 5 : 4
  const count = Math.min(words.length, maxPairs)
  const pairs = words.slice(-count).map(w => ({
    id: w.id,
    vietnamese: w.word_vietnamese,
    english: w.word_english,
    matched: false,
  }))
  matchPairs.value = pairs
  matchVnOrder.value = [...pairs].sort(() => Math.random() - 0.5)
  matchEnOrder.value = [...pairs].sort(() => Math.random() - 0.5)
}

// --- Quiz handlers ---

function selectQuizOption(option) {
  if (quizAnswered.value) return
  quizSelectedId.value = option.wordId
  quizAnswered.value = true
}

function submitTypeAnswer() {
  if (typeAnswerAnswered.value || !typeAnswerText.value.trim()) return
  const result = checkVietnameseAnswer(typeAnswerText.value, quizWord.value.word_vietnamese, [], { strict: strictTones })
  typeAnswerCorrect.value = result.isCorrect
  typeAnswerToneMismatch.value = result.toneMismatch
  typeAnswerAnswered.value = true
}

function selectFillOption(option) {
  if (fillAnswered.value) return
  fillSelectedText.value = option.text
  fillAnswered.value = true
}

function selectMatch(type, pair) {
  if (pair.matched || matchCompleted.value) return

  if (!matchSelected.value) {
    matchSelected.value = { type, id: pair.id }
    return
  }

  if (matchSelected.value.type === type) {
    matchSelected.value = { type, id: pair.id }
    return
  }

  // Selected from opposite column — check match
  if (matchSelected.value.id === pair.id) {
    const idx = matchPairs.value.findIndex(p => p.id === pair.id)
    matchPairs.value[idx].matched = true
    // Also update the display order arrays
    const vnIdx = matchVnOrder.value.findIndex(p => p.id === pair.id)
    if (vnIdx !== -1) matchVnOrder.value[vnIdx].matched = true
    const enIdx = matchEnOrder.value.findIndex(p => p.id === pair.id)
    if (enIdx !== -1) matchEnOrder.value[enIdx].matched = true
    matchSelected.value = null
    if (matchPairs.value.every(p => p.matched)) {
      matchCompleted.value = true
    }
  } else {
    matchSelected.value = null
  }
}

function advance() {
  if (phase.value === 'intro') {
    if (props.vocab.length === 0) {
      if (props.grammar.length === 0) { phase.value = 'complete'; return }
      phase.value = 'grammar'
      return
    }
    phase.value = 'vocab'
    wordIndex.value = 0
    revealed.value = autoReveal
    if (autoplayEnabled.value) nextTick(() => wordAudio.value?.play())
    return
  }

  if (phase.value === 'vocab') {
    learnedWords.value.push(props.vocab[wordIndex.value])
    const completedCount = wordIndex.value + 1
    emit('progress', { word: completedCount, total: props.vocab.length })

    if (shouldTriggerQuiz(completedCount, props.vocab.length)) {
      const type = pickQuizType()
      quizType.value = type

      if (type === 'multiple_choice') {
        quizWord.value = props.vocab[wordIndex.value]
        quizOptions.value = buildMultipleChoiceQuiz(quizWord.value, props.vocab)
        quizAnswered.value = false
        quizSelectedId.value = null
      } else if (type === 'reverse_recall') {
        quizWord.value = props.vocab[wordIndex.value]
        quizOptions.value = buildReverseRecallQuiz(quizWord.value, props.vocab)
        quizAnswered.value = false
        quizSelectedId.value = null
      } else if (type === 'fill_blank') {
        quizWord.value = props.vocab[wordIndex.value]
        const fb = buildFillBlankQuiz(quizWord.value, props.vocab)
        fillSentence.value = fb.sentence
        fillOptions.value = fb.options
        fillAnswered.value = false
        fillSelectedText.value = null
      } else if (type === 'type_answer') {
        quizWord.value = props.vocab[wordIndex.value]
        typeAnswerText.value = ''
        typeAnswerAnswered.value = false
        typeAnswerCorrect.value = false
        typeAnswerToneMismatch.value = false
      } else if (type === 'match_pairs') {
        quizWord.value = null
        buildMatchPairsQuiz(learnedWords.value)
        matchSelected.value = null
        matchCompleted.value = false
      }

      phase.value = 'quiz'
      return
    }

    wordIndex.value++
    revealed.value = autoReveal
    return
  }

  if (phase.value === 'quiz') {
    const nextWordIndex = wordIndex.value + 1

    if (nextWordIndex >= props.vocab.length) {
      if (props.grammar.length === 0) { phase.value = 'complete'; return }
      phase.value = 'grammar'
      grammarIndex.value = 0
      return
    }

    wordIndex.value = nextWordIndex
    revealed.value = autoReveal
    phase.value = 'vocab'
    return
  }

  if (phase.value === 'grammar') {
    const nextGrammarIndex = grammarIndex.value + 1
    if (nextGrammarIndex >= props.grammar.length) {
      phase.value = 'complete'
      return
    }
    grammarIndex.value = nextGrammarIndex
    return
  }
}
</script>
